//src/Components/Quickbooks/QuickbooksButton.tsx
import React, { useState, useEffect } from 'react';
import { CustomButton } from 'Components/Auth/CustomButton';
import { useGetMyProfileQuery } from 'app/services/user';
import { prepareHeaders } from 'utils';
import { successToast, errorToast } from 'Components/Notifications';
import useConfirmContinue from 'app/hooks/useConfirmContinue';
import { QBOIcon } from 'assets/icons';

/**
 * Custom hook to detect window width and trigger rerenders on resize.
 * Returns the current window width.
 */
function useWindowWidth() {
  const [width, setWidth] = useState(() => window.innerWidth);

  useEffect(() => {
    function handleResize() {
      setWidth(window.innerWidth);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return width;
}

const QBOConnectionButton: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirmContinue = useConfirmContinue();
  const { data: userData } = useGetMyProfileQuery();
  const user = userData?.user;

  // Get current window width
  const windowWidth = useWindowWidth();

  // Decide text size based on breakpoint (768px is "md" in Tailwind)
  const textSize = windowWidth < 768 ? 'text-xs' : 'text-sm';

  // Decide QuickBooks connection status
  const getQuickBooksStatus = () => {
    if (!user?.quickbooks?.quickbooks_token_expires_at) return 'Connect';
    const expiresAt = new Date(
      user.quickbooks.quickbooks_token_expires_at,
    ).getTime();
    return expiresAt < Date.now() ? 'Reconnect' : 'Connected';
  };

  const quickBooksButtonText = getQuickBooksStatus();

  // Disconnect QBO
  const handleDisconnectQuickBooks = async () => {
    try {
      setIsLoading(true);
      const headers = await prepareHeaders(new Headers());
      headers.append('Content-Type', 'application/json');

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/quick_books/disconnect`,
        { method: 'DELETE', headers },
      );

      if (response.ok) {
        successToast('QuickBooks disconnected successfully!');
        window.location.reload();
      } else {
        const errorData = await response.json();
        errorToast(errorData.error || 'Failed to disconnect QuickBooks');
      }
    } catch (err) {
      console.error('Error disconnecting QuickBooks:', err);
      errorToast('Error disconnecting QuickBooks');
    } finally {
      setIsLoading(false);
    }
  };

  // Confirm QBO disconnect
  const confirmDisconnectQuickBooks = () => {
    confirmContinue({
      title: 'Disconnect QuickBooks',
      text: (
        <div className="max-w-xs">
          Are you sure you want to disconnect from QBO?
        </div>
      ),
      onConfirm: handleDisconnectQuickBooks,
    });
  };

  // Connect or Reconnect QBO
  const handleConnectQuickBooks = async () => {
    const status = getQuickBooksStatus();

    // Try refreshing token first if status is Reconnect
    if (status === 'Reconnect') {
      try {
        setIsLoading(true);
        const headers = await prepareHeaders(new Headers());
        headers.append('Content-Type', 'application/json');

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/quick_books/refresh`,
          { method: 'PATCH', headers },
        );

        if (response.status === 204) {
          setError(null);
          successToast('QuickBooks token refreshed successfully!');
          window.location.reload();
          return; // stop here if refresh worked
        } else {
          const errorData = await response.json();
          errorToast(errorData.error || 'Failed to refresh QuickBooks token');
        }
      } catch (err) {
        console.error('Error refreshing QuickBooks token:', err);
        errorToast('QuickBooks refresh failed. Reconnecting...');
      } finally {
        setIsLoading(false);
      }
      // If refresh fails, continue to normal connect below
    }

    try {
      setIsLoading(true);
      const headers = await prepareHeaders(new Headers());
      headers.append('Content-Type', 'application/json');

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/quick_books/connect`,
        { method: 'GET', headers },
      );

      if (response.ok) {
        const data = await response.json();
        if (data.redirect_url) {
          window.open(data.redirect_url, '_blank');
        } else {
          errorToast('No redirect URL found');
        }
      } else {
        const errorData = await response.json();
        errorToast(errorData.error || 'Failed to connect');
      }
    } catch (err) {
      console.error('Error connecting QBO:', err);
      errorToast('Error connecting');
    } finally {
      setIsLoading(false);
    }
  };

  // Decide which operation (connect or disconnect) to do
  const handleQuickBooksButtonClick = () => {
    if (quickBooksButtonText === 'Connected') {
      confirmDisconnectQuickBooks();
    } else {
      handleConnectQuickBooks();
    }
  };

  return (
    <>
      <CustomButton
        text={quickBooksButtonText}
        bgColor="white"
        borderColor="transparent"
        textColor={quickBooksButtonText === 'Connected' ? 'gray-700' : 'green'}
        textSize={textSize}
        // Add a smaller height prop to shrink the button's height (e.g., "h-8" instead of the default "h-12")
        height="h-8"
        prefix={<QBOIcon className="w-4 h-4 md:w-5 md:h-5" />}
        onClick={handleQuickBooksButtonClick}
        disabled={isLoading}
      />
      {error && <p className="text-red-500 ml-4">{error}</p>}
    </>
  );
};

export default QBOConnectionButton;
